import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';

import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';

import { DateUtils } from '@shared/utils/date/date.utils';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';

import { HeaderService } from '@core/services/header/header.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';

@Component({
  selector: 'app-shop-search-page',
  templateUrl: './shop-search-page.component.html',
  standalone: false,
})
export class ShopSearchPageComponent implements OnInit, OnDestroy {
  public body = input<any>();
  public defaultLatitude = input<number>();
  public defaultLongitude = input<number>();
  public defaultMaxDistance = input<number>();
  public backgroundColor = input<IContentBuilderFieldColor>();
  public backgroundImage = input<IContentBuilderFieldImage>();
  public metaDescription = input<string>();

  private readonly meta = inject(Meta);
  private shopSelector = inject(ShopStoreSelector);
  private shopDispatcher = inject(ShopStoreDispatcher);
  private readonly headerService = inject(HeaderService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navigationHistoryService = inject(NavigationHistoryService);
  private readonly shopSearchParametersService = inject(ShopSearchParametersService);

  ngOnInit(): void {
    const shopActive = this.shopSelector.active;

    if (shopActive) {
      this.shopDispatcher.removeOne(shopActive.id);
    }

    this.headerService.toggleShopSearchParametersVisibility(true);

    this.shopSearchParametersService.setDefault({
      latitude: this.defaultLatitude(),
      longitude: this.defaultLongitude(),
      maxDistance: this.defaultMaxDistance(),
    });

    if (this.metaDescription()) {
      this.meta.updateTag({
        name: 'description',
        content: this.metaDescription() || '',
      });
    }

    this.activatedRoute.queryParams.subscribe(queryParams => {
      if (queryParams.latitude && queryParams.longitude) {
        this.shopSearchParametersService.set({
          location: {
            address: queryParams.address,
            lat: queryParams.latitude,
            lng: queryParams.longitude,
          },
        });
      }
      if (queryParams.distance) {
        this.shopSearchParametersService.set({
          distance: queryParams.distance,
        });
      }
      if (queryParams.distribution_mode) {
        this.shopSearchParametersService.set({
          distribution_mode: queryParams.distribution_mode,
        });
      }
      if (queryParams.wanted_at_type) {
        this.shopSearchParametersService.set({
          wanted_at: new Date(DateUtils.dayjsInMarketplaceTimezone(queryParams.wanted_at).format()),
        });
      }

      const urlQueryParams = new URLSearchParams(queryParams).toString();
      this.navigationHistoryService.pushState(
        'shop-search',
        '/' + this.activatedRoute.snapshot.url.join('/') + '?' + urlQueryParams,
      );
    });
  }

  ngOnDestroy(): void {
    this.headerService.toggleShopSearchParametersVisibility(false);
  }

  getBackgroundStyles() {
    return {
      'background-color': this.backgroundColor()
        ? fromContentBuilderColor(this.backgroundColor())
        : null,
      'background-image': this.backgroundImage()?.url
        ? `url(${this.backgroundImage()!.url})`
        : null,
      'background-size': this.backgroundImage()?.url ? 'cover' : null,
    };
  }
}
